import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig.js";
import { toast } from "react-toastify";
import "./LoginHistoryModal.css";

const API_URL = process.env.REACT_APP_API_URL;

const LoginHistoryModal = ({ userId, userName, onClose }) => {
  const [sessions, setSessions] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLoginHistory();
  }, [userId]);

  const fetchLoginHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/users/${userId}/login-history`);
      setSessions(response.data.sessions);
      setUser(response.data.user);
    } catch (error) {
      console.error("Error fetching login history:", error);
      toast.error("Fehler beim Laden der Login-Historie");
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);
    const now = new Date();

    // Format date and time
    const timeStr = date.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });

    // Check if today
    const isToday = date.toDateString() === now.toDateString();

    // Check if yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = date.toDateString() === yesterday.toDateString();

    if (isToday) {
      return `Heute, ${timeStr}`;
    } else if (isYesterday) {
      return `Gestern, ${timeStr}`;
    } else {
      const dateStr = date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      });
      return `${dateStr}, ${timeStr}`;
    }
  };

  const parseBrowser = (userAgent) => {
    if (!userAgent || userAgent === "Unknown") return "Unbekannt";

    // Simple browser detection
    if (userAgent.includes("Edg/")) return "Edge";
    if (userAgent.includes("Chrome/")) return "Chrome";
    if (userAgent.includes("Firefox/")) return "Firefox";
    if (userAgent.includes("Safari/") && !userAgent.includes("Chrome")) return "Safari";
    if (userAgent.includes("Opera/") || userAgent.includes("OPR/")) return "Opera";

    return "Anderer Browser";
  };

  const parseOS = (userAgent) => {
    if (!userAgent || userAgent === "Unknown") return "";

    if (userAgent.includes("Windows")) return "Windows";
    if (userAgent.includes("Mac OS")) return "macOS";
    if (userAgent.includes("Linux")) return "Linux";
    if (userAgent.includes("Android")) return "Android";
    if (userAgent.includes("iOS") || userAgent.includes("iPhone") || userAgent.includes("iPad")) return "iOS";

    return "";
  };

  const formatDevice = (userAgent) => {
    const browser = parseBrowser(userAgent);
    const os = parseOS(userAgent);

    if (browser === "Unbekannt") return "Unbekannt";

    return os ? `${browser} (${os})` : browser;
  };

  const formatTime = (dateString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);
    const now = new Date();

    // Format time
    const timeStr = date.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit"
    });

    // Check if today
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return `Heute, ${timeStr}`;
    } else {
      const dateStr = date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit"
      });
      return `${dateStr}, ${timeStr}`;
    }
  };

  // Close modal when clicking outside
  const handleBackdropClick = (e) => {
    if (e.target.className === "login-history-modal-backdrop") {
      onClose();
    }
  };

  return (
    <div className="login-history-modal-backdrop" onClick={handleBackdropClick}>
      <div className="login-history-modal">
        <div className="login-history-modal-header">
          <h2>📜 Login-Historie</h2>
          <p className="login-history-user-name">{userName}</p>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="login-history-modal-content">
          {loading ? (
            <div className="loading-state">Lädt...</div>
          ) : sessions.length === 0 ? (
            <div className="empty-state">
              <p>Keine Login-Historie gefunden</p>
              <span className="hint">
                Dieser Benutzer hat sich noch nicht angemeldet oder die Historie wurde gelöscht.
              </span>
            </div>
          ) : (
            <div className="history-table-container">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Login-Zeit</th>
                    <th>Browser / Gerät</th>
                    <th>Letzte Aktivität</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session, index) => (
                    <tr key={session._id || index}>
                      <td className="session-number">{index + 1}</td>
                      <td className="session-time">
                        {formatDateTime(session.loginTime)}
                      </td>
                      <td className="session-device">
                        {formatDevice(session.userAgent)}
                      </td>
                      <td className="activity-time">
                        {index === 0 ? formatTime(user?.lastActivity) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="history-footer">
                <p>
                  Zeige die letzten {sessions.length} {sessions.length === 1 ? "Login" : "Logins"}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginHistoryModal;
